import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import RootLayout from "@/layouts/root-layout";
import Home from "@/pages/Home";
import HomeMobile from "@/pages/HomeMobile";
import Catalog from "@/pages/Catalog";
import CittaDiagram from "@/pages/diagrams/Citta";
import CetasikaDiagram from "@/pages/diagrams/Cetasika";
import RupaDiagram from "@/pages/diagrams/Rupa";
import FourNobleTruthsDiagram from "@/pages/diagrams/FourNobleTruths";
import EightfoldPathDiagram from "@/pages/diagrams/EightfoldPath";
import DependentOriginationDiagram from "@/pages/diagrams/DependentOrigination";
import FiveAggregatesDiagram from "@/pages/diagrams/FiveAggregates";
import ThreeMarksDiagram from "@/pages/diagrams/ThreeMarks";
import ThreeTrainingsDiagram from "@/pages/diagrams/ThreeTrainings";
import BuddhistMeditationDiagram from "@/pages/diagrams/BuddhistMeditation";
import FourImmeasurablesDiagram from "@/pages/diagrams/FourImmeasurables";
import KammaVipakaDiagram from "@/pages/diagrams/KammaVipaka";
import FourStagesDiagram from "@/pages/diagrams/FourStages";
import AbhidhammaCittaDiagram from "@/pages/diagrams/AbhidhammaCitta";
import CoursesCatalogPage from "@/pages/CoursesCatalogPage";
import CourseDetailPage from "@/pages/courses/CourseDetailPage";
import CourseLessonPage from "@/pages/CourseLessonPage";

// Import các trang mới
import CoursesPage from "@/pages/courses/CoursesPage";
import CourseDetailPage2 from "@/pages/courses/CourseDetailPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import RegisterSuccessPage from "@/pages/RegisterSuccessPage";
import BuddhistSchoolQuiz from "@/pages/buddhist-school/BuddhistSchoolQuiz";
import VerifyCertificatePage from "@/pages/VerifyCertificatePage";
import NewsList from "@/pages/NewsList";
import NewsDetail from "@/pages/NewsDetail";

// Import các trang user
import ProfilePage from "@/pages/ProfilePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionThankYouPage from "@/pages/SubscriptionThankYouPage";
import LearningDashboardPage from "@/pages/LearningDashboardPage";
import AIChatPage from "@/pages/AIChatPage";
import GamificationPage from "@/pages/GamificationPage";
import CreditsHistoryPage from "@/pages/CreditsHistoryPage";
import PaymentPage from "@/pages/PaymentPage";

// Import admin routes
import { lazy, Suspense } from "react";
import DiagramDetail from "@/pages/diagrams/DiagramDetail";
import LanguagesPage from "@/admin/pages/Languages";
import ExpertManagementPage from "@/admin/pages/ExpertManagementPage";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import PWAInstallPrompt from "@/components/ui/PWAInstallPrompt";
import useMobileDetect from "@/hooks/useMobileDetect";

// Import swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './styles/swiper-styles.css';

// Lazy load admin components
const AdminLayout = lazy(() => import("@/admin/components/Layout/AdminLayout"));
const Dashboard = lazy(() => import("@/admin/pages/Dashboard"));
const Posts = lazy(() => import("@/admin/pages/Posts"));
const EditPost = lazy(() => import("@/admin/pages/EditPost"));
const Categories = lazy(() => import("@/admin/pages/Categories"));
const JsonManager = lazy(() => import("@/admin/pages/JsonManager"));
const Users = lazy(() => import("@/admin/pages/Users"));
const Courses = lazy(() => import("@/admin/pages/Courses"));
const EditCourse = lazy(() => import("@/admin/pages/EditCourse"));
const CacheManager = lazy(() => import("@/admin/pages/CacheManager"));
const Diagrams = lazy(() => import("@/admin/pages/Diagrams"));
const EditDiagram = lazy(() => import("@/admin/pages/EditDiagram"));
const Settings = lazy(() => import("@/admin/pages/Settings"));
const QuizGames = lazy(() => import("@/admin/pages/QuizGames"));

// Loading component
const Loading = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center h-screen">
      {t("loading", { ns: "common" })}
    </div>
  );
};

// Home component that conditionally renders mobile or desktop version
export const HomeRouter = () => {
  const { isMobile } = useMobileDetect();
  
  // Thêm debug log
  console.log('HomeRouter render:', { isMobile });
  
  // Return the appropriate home component based on device
  return isMobile ? <HomeMobile /> : <Home />;
};

function App() {
  return (
    <>
      <Routes>
        <Route path="/" element={<RootLayout />}>
          <Route index element={<HomeRouter />} />
          <Route path="catalog" element={<Catalog />} />

          {/* Auth routes */}
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="register-success" element={<RegisterSuccessPage />} />

          {/* User routes */}
          <Route
            path="profile"
            element={<Navigate to="/learning-dashboard?tab=profile" replace />}
          />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route
            path="subscription/thank-you"
            element={<SubscriptionThankYouPage />}
          />
          <Route path="credits/history" element={<CreditsHistoryPage />} />
          <Route path="payment" element={<PaymentPage />} />
          <Route
            path="learning-dashboard"
            element={<LearningDashboardPage />}
          />
          <Route path="gamification" element={<GamificationPage />} />
          <Route path="ai-chat" element={<AIChatPage />} />
          <Route path="ai-chat/:chatId" element={<AIChatPage />} />

          {/* Certificate verification */}
          <Route
            path="verify-certificate"
            element={<VerifyCertificatePage />}
          />
          <Route
            path="verify-certificate/:certificateId"
            element={<VerifyCertificatePage />}
          />

          {/* Buddhist School Quiz */}
          <Route path="buddhist-school/quiz" element={<BuddhistSchoolQuiz />} />

          {/* Courses */}
          <Route path="courses" element={<CoursesPage />} />
          <Route
            path="courses/category/:categorySlug"
            element={<CoursesPage />}
          />
          <Route path="courses/:courseId" element={<CourseDetailPage2 />} />

          {/* Old Courses */}
          <Route path="courses-old" element={<CoursesCatalogPage />} />
          <Route
            path="courses-old/category/:categoryId"
            element={<CoursesCatalogPage />}
          />
          <Route path="courses-old/:courseId" element={<CourseDetailPage />} />
          <Route
            path="courses-old/:courseId/lesson/:lessonId"
            element={<CourseLessonPage />}
          />

          {/* Diagrams */}
          <Route path="diagrams/citta" element={<CittaDiagram />} />
          <Route path="diagrams/cetasika" element={<CetasikaDiagram />} />
          <Route path="diagrams/:id" element={<DiagramDetail />} />

          {/* News routes */}
          <Route path="news">
            <Route index element={<NewsList />} />
            <Route path=":slug" element={<NewsDetail />} />
          </Route>

          {/* Better 404 page */}
          <Route path="*" element={
            <div className="container mx-auto py-20 text-center">
              <h1 className="text-4xl font-bold mb-4">404 - Không tìm thấy trang</h1>
              <p className="mb-8">Trang bạn đang tìm kiếm không tồn tại hoặc đã được di chuyển.</p>
              <Button asChild>
                <Link to="/">Quay về trang chủ</Link>
              </Button>
            </div>
          } />
        </Route>

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <Suspense fallback={<Loading />}>
              <AdminLayout />
            </Suspense>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<Loading />}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path="dashboard"
            element={
              <Suspense fallback={<Loading />}>
                <Dashboard />
              </Suspense>
            }
          />
          <Route
            path="posts"
            element={
              <Suspense fallback={<Loading />}>
                <Posts />
              </Suspense>
            }
          />
          <Route
            path="posts/new"
            element={
              <Suspense fallback={<Loading />}>
                <EditPost />
              </Suspense>
            }
          />
          <Route
            path="posts/edit/:id"
            element={
              <Suspense fallback={<Loading />}>
                <EditPost />
              </Suspense>
            }
          />
          <Route
            path="categories"
            element={
              <Suspense fallback={<Loading />}>
                <Categories />
              </Suspense>
            }
          />
          <Route
            path="json-manager"
            element={
              <Suspense fallback={<Loading />}>
                <JsonManager />
              </Suspense>
            }
          />
          <Route
            path="users"
            element={
              <Suspense fallback={<Loading />}>
                <Users />
              </Suspense>
            }
          />
          <Route
            path="courses"
            element={
              <Suspense fallback={<Loading />}>
                <Courses />
              </Suspense>
            }
          />
          <Route
            path="courses/new"
            element={
              <Suspense fallback={<Loading />}>
                <EditCourse />
              </Suspense>
            }
          />
          <Route
            path="courses/edit/:id"
            element={
              <Suspense fallback={<Loading />}>
                <EditCourse />
              </Suspense>
            }
          />
          <Route
            path="diagrams"
            element={
              <Suspense fallback={<Loading />}>
                <Diagrams />
              </Suspense>
            }
          />
          <Route
            path="diagrams/new"
            element={
              <Suspense fallback={<Loading />}>
                <EditDiagram />
              </Suspense>
            }
          />
          <Route
            path="diagrams/:id/edit"
            element={
              <Suspense fallback={<Loading />}>
                <EditDiagram />
              </Suspense>
            }
          />
          <Route
            path="cache"
            element={
              <Suspense fallback={<Loading />}>
                <CacheManager />
              </Suspense>
            }
          />
          <Route
            path="quiz-games"
            element={
              <Suspense fallback={<Loading />}>
                <QuizGames />
              </Suspense>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<Loading />}>
                <Settings />
              </Suspense>
            }
          />
          <Route
            path="languages"
            element={
              <Suspense fallback={<Loading />}>
                <LanguagesPage />
              </Suspense>
            }
          />
          <Route
            path="experts"
            element={
              <Suspense fallback={<Loading />}>
                <ExpertManagementPage />
              </Suspense>
            }
          />
        </Route>
      </Routes>
      <Toaster />
      <PWAInstallPrompt />
    </>
  );
}

export default App;
